import moment from 'moment';
import jwtEncode from 'jwt-encode';
import React, { useEffect } from 'react';

import Config from 'Config';
import validations from './validations';
import { canSignResults, recoverOfflineResults, withResultUser } from './offlineResults';

import { DATE_FORMAT, ROLES } from 'Constants';

const adminMode = user => ROLES.ADMINS.includes(user?.role_name);

const generateComponentProperties = properties => {
  let required = false;
  let showInSummaryPage = false;
  let showExtraInfo2 = false;
  let max_length = null;

  for (let i = 0; i < properties?.length; i++) {
    const { property, value } = properties[i];
    if (property === 'required') {
      required = stringToBool(value);
    }

    if (property === 'show_in_summary_page') {
      showInSummaryPage = stringToBool(value);
    }

    if (property === 'max_length') {
      max_length = parseInt(value) || null;
    }

    if (property === 'show_extra_info_2') {
      showExtraInfo2 = stringToBool(value);
    }
  }

  return { max_length, required, showInSummaryPage, showExtraInfo2 };
};

const getAge = birthDateString => {
  const today = new Date();
  const birthDate = new Date(birthDateString.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$2/$1/$3'));
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const getEffectiveQuickFilters = (
  userAdminFilters = {},
  adminFilters = {},
  filtersType = '',
  campaignType = '',
  view = ''
) => {
  const campaignKey = campaignType.toLowerCase();
  const viewKey = view.toLowerCase();

  const specificUserFilters =
    campaignType && view ? userAdminFilters?.[campaignKey]?.[viewKey]?.[filtersType] : userAdminFilters?.[filtersType];

  const hasUserFilters = specificUserFilters && Object.keys(specificUserFilters).length > 0;
  const filters = hasUserFilters ? userAdminFilters?.[campaignKey]?.[viewKey] || {} : adminFilters;

  const yesterday = moment().subtract(1, 'days').format(DATE_FORMAT.INTERNAL);
  const dateFilters = filters?.[filtersType]?.date;

  //set default date to yesterday by default
  if (dateFilters?.length) {
    filters[filtersType].date = dateFilters.map(filter => ({
      ...filter,
      defaultDate: yesterday
    }));
  }

  return filters;
};

const getNestedValue = (object = {}, key = '', fallbackValue = undefined) =>
  key.split('.').reduce((nestedValue, currentKey) => {
    if (nestedValue !== undefined) {
      return nestedValue?.[currentKey];
    } else {
      return fallbackValue;
    }
  }, object);

const checkSearchParams = search => {
  const params = new URLSearchParams(search?.slice(1));
  return {
    ba: params.get('ba'),
    client: params.get('client'),
    confirmation_token: params.get('confirmation_token'),
    country: params.get('country'),
    isDonor: params.has('donor'),
    locale: params.get('locale'),
    mc_email: params.get('mc_email'),
    mc_name: params.get('mc_name'),
    orID: params.get('token'),
    primaryLogo: params.get('primary_logo_url'),
    redirect_token: params.get('redirect_token'),
    resetPasswordToken: params.get('reset_password_token')
  };
};

const clearAppCache = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(regs => regs.forEach(reg => reg.unregister()));
  }

  if ('caches' in window) {
    caches.keys().then(names => names.forEach(name => caches.delete(name)));
  }
};

function debounce(func, delay) {
  let debounceTimer;
  return function (...args) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func.apply(this, args), delay);
  };
}

const defaultMcPin = (mc_pins = [], mc_ids = [], managed_mc_id = '') => {
  const defaultIdIndex = mc_ids.findIndex(id => id === managed_mc_id);

  return mc_pins[defaultIdIndex];
};

const displayedValue = (value = {}, displayProperty = '') => (displayProperty ? value[displayProperty] : value);

const encodeData = (data, key) => jwtEncode(data, `${Config.encodeKey}${key}`);

const idFilter = (choice, rf) => choice === rf.id;

const isAndroidInAppBrowser =
  /Android/i.test(navigator.userAgent) && new URLSearchParams(window.location.search).has('redirect_token');

const disableSafeAreaForAndroidInAppBrowser = () => {
  if (!isAndroidInAppBrowser) return;

  const viewport = document.querySelector('meta[name="viewport"]');
  if (viewport) {
    viewport.setAttribute('content', viewport.getAttribute('content').replace(/,?\s*viewport-fit=cover/i, ''));
  }

  const root = document.documentElement;
  root.style.setProperty('--f7-safe-area-top', '0px');
  root.style.setProperty('--f7-safe-area-bottom', '0px');
};

const isSmallDevice = Config.SMALL_DEVICE_WIDTH > window.innerWidth;

const isExtraSmallDevice = Config.EXTRA_SMALL_DEVICE_WIDTH > window.innerWidth;

const isObjEmpty = obj => Object.entries(obj).length === 0;

const ibanMaskConfig = iban => {
  const groupSize = 4;
  return iban
    ?.replace(/[^a-zA-Z0-9]/g, '')
    ?.replace(new RegExp(`(.{${groupSize}})(?!$)`, 'g'), '$1 ')
    ?.trim();
};

const preventDefault = event => {
  event.preventDefault();
};

const isValidIBAN = iban => {
  const CODE_LENGTHS = {
    AD: 24,
    AE: 23,
    AT: 20,
    AZ: 28,
    BA: 20,
    BE: 16,
    BG: 22,
    BH: 22,
    BR: 29,
    CH: 21,
    CR: 22,
    CY: 28,
    CZ: 24,
    DE: 22,
    DK: 18,
    DO: 28,
    EE: 20,
    ES: 24,
    FI: 18,
    FO: 18,
    FR: 27,
    GB: 22,
    GI: 23,
    GL: 18,
    GR: 27,
    GT: 28,
    HR: 21,
    HU: 28,
    IE: 22,
    IL: 23,
    IS: 26,
    IT: 27,
    JO: 30,
    KW: 30,
    KZ: 20,
    LB: 28,
    LI: 21,
    LT: 20,
    LU: 20,
    LV: 21,
    MC: 27,
    MD: 24,
    ME: 22,
    MK: 19,
    MR: 27,
    MT: 31,
    MU: 30,
    NL: 18,
    NO: 15,
    PK: 24,
    PL: 28,
    PS: 29,
    PT: 25,
    QA: 29,
    RO: 24,
    RS: 22,
    SA: 24,
    SE: 24,
    SI: 19,
    SK: 24,
    SM: 27,
    TN: 24,
    TR: 26
  };
  iban = iban.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/);
  if (!code || iban.length !== CODE_LENGTHS[code[1]]) {
    return false;
  }
  const digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, function (letter) {
    return letter.charCodeAt(0) - 55;
  });
  if (mod97(digits) !== 1) {
    return false;
  }
  return true;
};

const mod97 = digits => {
  let checksum = digits.slice(0, 2);
  let fragment;
  for (let offset = 2; offset < digits.length; offset += 7) {
    fragment = `${checksum}${digits.substring(offset, offset + 7)}`;
    checksum = parseInt(fragment, 10) % 97;
  }
  return checksum;
};

const mockComponent = () => <div></div>;

const mockFunction = () => {};

const regularPagesOnly = page => page.page_type === 'regular';

function sortedDates(allResults, sortByDate) {
  return allResults?.sort((a, b) => {
    const firstDate = new Date(a.submitted_at).getTime();
    const lastDate = new Date(b.submitted_at).getTime();
    return sortByDate ? lastDate - firstDate : firstDate - lastDate;
  });
}

const stringToBool = val => val == 'true';

const sum = (a, b) => a + b;

const thankYouPageOnly = page => page.page_type === 'thank_you';

const capitalize = string => string && string?.charAt(0)?.toUpperCase() + string?.slice(1);

const blacklist = {
  transientReset: [
    'AdminView',
    'AdvancedFilters',
    'BAsView',
    'Birthday',
    'BulkReleaseBlock',
    'CampaignsView',
    'CreateUser',
    'DailyFigures',
    'Dashboard',
    'ForgotPassword',
    'FullName',
    'OnboardingProfile',
    'PostCodesList',
    'Profile',
    'RejectDocument',
    'RejectSection',
    'RequestPasswordConfirmation',
    'ResetPassword',
    'SearchAddressList',
    'SignUp',
    'TermsAndConditions',
    'ThankYou',
    'DailyFigures',
    'BAsView',
    'CampaignsView',
    'AdminView'
  ]
};

const handleOnChange = (updateTransientProps, key, e) => {
  updateTransientProps({ [key]: e.target?.value });
};

const handleOnChangeWithValidations = (updateTransientPropWithValidations, key, validations, e) => {
  updateTransientPropWithValidations(key, e.target.value, validations);
};

const onFieldChange = (updateTransientProps, key, value) => {
  updateTransientProps({ [key]: value });
};

const camelToSnake = camelStr => camelStr?.replace(/([A-Z])/g, '_$1').toLowerCase();

const calculateTimeSince = dateString => {
  const currentDate = moment();
  const givenDate = moment(dateString);

  const differenceInDays = currentDate.diff(givenDate, 'days');
  const differenceInHours = currentDate.diff(givenDate, 'hours');
  const differenceInMinutes = currentDate.diff(givenDate, 'minutes');

  return {
    days: differenceInDays,
    hours: differenceInHours % 24,
    minutes: differenceInMinutes % 60
  };
};

const clearStorage = () => {
  const persistedData = localStorage.getItem('persist:databyte-pwa-persist');
  if (persistedData) {
    const parsedData = JSON.parse(persistedData);

    const application = parsedData.application ?? {};
    const parsedApplication = typeof application === 'string' ? JSON.parse(application) : application;

    const result = parsedData.result ?? {};
    const parsedResults = typeof result === 'string' ? JSON.parse(result) : result;

    const offlineResults = parsedResults[parsedApplication.lastKnownUserId]?.offline;

    localStorage.clear();

    const dataToPersist = {
      application: parsedApplication,
      ...(offlineResults?.length && { offlineResults: offlineResults })
    };

    localStorage.setItem('persist:databyte-pwa-persist', JSON.stringify(dataToPersist));
  }
};

const phoneNumberSanitization = event => {
  const inputValue = event.target.value;
  const cleanedValue = inputValue.replace(/[^\d]/g, ''); // Remove non-numeric characters

  if (cleanedValue !== inputValue) {
    event.target.value = cleanedValue;
  }
};

const valueSanitization = event => {
  const inputValue = event.target.value;
  const cleanedValue = inputValue.replace(/^\s+|\s+$/g, '').replace(/\s+/g, ' '); // Remove unnecessary spaces

  if (cleanedValue !== inputValue) {
    event.target.value = cleanedValue;
  }

  return cleanedValue;
};

const FormattedLabel = ({ name = '', required = false }) => (
  <div className="flex">
    <div>{name}</div>
    {required && <div className="text-fail text-md pl-4">*</div>}
  </div>
);

const formatDate = dateString => {
  if (!dateString) return null;
  return moment(dateString).format(DATE_FORMAT.UI);
};

const stopPropagation = event => event.stopPropagation();

const useOutsideClick = (ref, callback) => {
  useEffect(() => {
    const handleClickOutside = event => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
};

const getStageQueryParams = stages => {
  if (!stages || stages.length === 0) return '';
  return `&${stages.map(stage => `by_role_names%5B%5D=${encodeURIComponent(stage)}`).join('&')}`;
};

const isUpdateRequired = (latest, installed) => {
  const [latestMajor, latestMinor, latestPatch] = (latest?.split('.') || [])?.map(Number);
  const [installedMajor, installedMinor, installedPatch] = (installed?.split('.') || [])?.map(Number);

  return (
    latestMajor > installedMajor ||
    (latestMajor === installedMajor && latestMinor > installedMinor) ||
    (latestMajor === installedMajor && latestMinor === installedMinor && latestPatch > installedPatch)
  );
};

const fileToDataURL = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

export {
  adminMode,
  blacklist,
  calculateTimeSince,
  camelToSnake,
  canSignResults,
  capitalize,
  checkSearchParams,
  clearAppCache,
  clearStorage,
  debounce,
  disableSafeAreaForAndroidInAppBrowser,
  defaultMcPin,
  displayedValue,
  encodeData,
  fileToDataURL,
  formatDate,
  FormattedLabel,
  generateComponentProperties,
  getAge,
  getEffectiveQuickFilters,
  getNestedValue,
  getStageQueryParams,
  handleOnChange,
  handleOnChangeWithValidations,
  ibanMaskConfig,
  idFilter,
  isAndroidInAppBrowser,
  isExtraSmallDevice,
  isObjEmpty,
  isSmallDevice,
  isUpdateRequired,
  isValidIBAN,
  mockComponent,
  mockFunction,
  onFieldChange,
  phoneNumberSanitization,
  preventDefault,
  recoverOfflineResults,
  regularPagesOnly,
  sortedDates,
  stopPropagation,
  stringToBool,
  sum,
  thankYouPageOnly,
  useOutsideClick,
  validations,
  valueSanitization,
  withResultUser
};
